import React, { useEffect, useRef, useState } from "react";

interface FadeAlertProps {
  alert: string | null;
  type: FadeAlertType;
  onClose: () => void;
}

export const FadeAlertType = {
  success: "success",
  warning: "warning",
  error: "error",
  info: "info",
} as const;

export type FadeAlertType = (typeof FadeAlertType)[keyof typeof FadeAlertType];

const FadeAlert: React.FC<FadeAlertProps> = ({ alert, type, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const timersRef = useRef<NodeJS.Timeout[]>([]);

  // Clean up all timers
  const clearAllTimers = () => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current = [];
  };

  useEffect(() => {
    // Clean up previous timers
    clearAllTimers();

    if (alert) {
      // Show alert immediately
      setIsVisible(true);
      setIsExiting(false);

      // Start fade-out animation after 3 seconds
      const fadeOutTimer = setTimeout(() => {
        setIsExiting(true);
      }, 3000);
      timersRef.current.push(fadeOutTimer);

      // Hide alert after fade-out animation completes (3s + 0.5s animation)
      const hideTimer = setTimeout(() => {
        setIsVisible(false);
        setIsExiting(false);
        onClose();
      }, 3500);
      timersRef.current.push(hideTimer);
    } else {
      // Alert is null - clean up all state
      setIsVisible(false);
      setIsExiting(false);
    }

    // Cleanup function
    return () => {
      clearAllTimers();
    };
  }, [alert]); // Only depend on alert, not onClose

  if (!isVisible) return null;

  const getAlertColor = () => {
    switch (type) {
      case FadeAlertType.success:
        return "bg-green-600 dark:bg-green-700";
      case FadeAlertType.error:
        return "bg-rose-600 dark:bg-rose-700";
      case FadeAlertType.warning:
        return "bg-yellow-600 dark:bg-yellow-700";
      case FadeAlertType.info:
        return "bg-blue-600 dark:bg-blue-700";
      default:
        return "bg-blue-600 dark:bg-blue-700";
    }
  };

  const getAlertIcon = () => {
    switch (type) {
      case FadeAlertType.success:
        return "✓";
      case FadeAlertType.error:
        return "✕";
      case FadeAlertType.warning:
        return "⚠";
      case FadeAlertType.info:
        return "ℹ";
      default:
        return "→";
    }
  };

  return (
    <div
      className={`
        z-9999 fixed bottom-4 left-1/2 flex -translate-x-1/2 transform items-center 
        gap-3 rounded-lg px-6 py-4 text-white shadow-lg
        transition-all duration-500 ease-in-out
        ${getAlertColor()}
        ${isExiting ? "translate-y-2 opacity-0" : "translate-y-0 opacity-100"}
      `}
      role="alert"
      aria-live="polite"
    >
      <span className="flex-shrink-0 text-lg font-bold">{getAlertIcon()}</span>
      <span className="flex-grow text-sm font-medium">{alert}</span>
    </div>
  );
};

export default FadeAlert;
